import { OFFLINE_REWARD_ART } from "../config/artAssets";

export type OfflineRewardWarmupStatus = "idle" | "loading" | "ready" | "error";

export type OfflineRewardWarmupSnapshot = {
  status: OfflineRewardWarmupStatus;
  completed: number;
  failed: number;
  total: number;
  progress: number;
};

export type OfflineRewardWarmupController = {
  getSnapshot(): OfflineRewardWarmupSnapshot;
  subscribe(listener: (snapshot: OfflineRewardWarmupSnapshot) => void): () => void;
  warm(): Promise<OfflineRewardWarmupSnapshot>;
  reset(): boolean;
};

type AssetLoader = (url: string) => Promise<void>;

export const OFFLINE_REWARD_ASSET_URLS = [
  OFFLINE_REWARD_ART.background.url,
  OFFLINE_REWARD_ART.astrolabe.core.url,
  OFFLINE_REWARD_ART.astrolabe.outerRing.url,
  OFFLINE_REWARD_ART.astrolabe.innerRing.url,
] as const;

function snapshot(
  status: OfflineRewardWarmupStatus,
  completed: number,
  failed: number,
  total: number,
): OfflineRewardWarmupSnapshot {
  return Object.freeze({
    status,
    completed,
    failed,
    total,
    progress: total === 0 ? 1 : completed / total,
  });
}

export function createOfflineRewardWarmupController(
  urls: readonly string[],
  loadAsset: AssetLoader,
): OfflineRewardWarmupController {
  const uniqueUrls = [...new Set(urls)];
  const listeners = new Set<(value: OfflineRewardWarmupSnapshot) => void>();
  let current = snapshot(uniqueUrls.length === 0 ? "ready" : "idle", 0, 0, uniqueUrls.length);
  let active: Promise<OfflineRewardWarmupSnapshot> | null = null;

  const publish = (next: OfflineRewardWarmupSnapshot): void => {
    current = next;
    listeners.forEach((listener) => listener(current));
  };

  return {
    getSnapshot: () => current,
    subscribe(listener) {
      listeners.add(listener);
      listener(current);
      return () => listeners.delete(listener);
    },
    warm() {
      if (current.status === "ready" || current.status === "error") return Promise.resolve(current);
      if (active !== null) return active;

      let completed = 0;
      let failed = 0;
      publish(snapshot("loading", completed, failed, uniqueUrls.length));
      active = Promise.all(
        uniqueUrls.map(async (url) => {
          try {
            await loadAsset(url);
          } catch {
            failed += 1;
          } finally {
            completed += 1;
            publish(snapshot("loading", completed, failed, uniqueUrls.length));
          }
        }),
      ).then(() => {
        const result = snapshot(failed === 0 ? "ready" : "error", completed, failed, uniqueUrls.length);
        publish(result);
        return result;
      });
      return active;
    },
    reset() {
      if (current.status === "loading") return false;
      active = null;
      publish(snapshot(uniqueUrls.length === 0 ? "ready" : "idle", 0, 0, uniqueUrls.length));
      return true;
    },
  };
}

const decodedImages = new Map<string, HTMLImageElement>();

function loadAndDecodeImage(url: string): Promise<void> {
  const cached = decodedImages.get(url);
  if (cached?.complete === true && cached.naturalWidth > 0) return Promise.resolve();

  return new Promise<void>((resolve, reject) => {
    const image = new Image();
    image.decoding = "async";
    image.fetchPriority = "high";
    image.onload = () => {
      const decode = typeof image.decode === "function" ? image.decode().catch(() => undefined) : Promise.resolve();
      void decode.then(() => {
        decodedImages.set(url, image);
        resolve();
      });
    };
    image.onerror = () => reject(new Error(`Offline reward asset failed to load: ${url}`));
    image.src = url;
  });
}

export const offlineRewardAssetWarmup = createOfflineRewardWarmupController(
  OFFLINE_REWARD_ASSET_URLS,
  loadAndDecodeImage,
);
